import {
  createContext,
  type Dispatch,
  type FC,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {FormValidations} from '~/common/FabricInvitation/types';
import type {UpsellMode} from '~/common/fabric/UpsellModal/UpsellModal';
import type {EventFeature} from '~/common/types/event';

interface FabricInvitationContextType {
  isPremiumOnMount: boolean;
  formValidations: RefObject<FormValidations>;
  setFormValidations: (validations: (current: FormValidations) => FormValidations) => void;
  featureToggling: EventFeature | null;
  setFeatureToggling: Dispatch<SetStateAction<EventFeature | null>>;
  showUpsellModal: boolean;
  setShowUpsellModal: Dispatch<SetStateAction<boolean>>;
  upsellMode?: UpsellMode;
  setUpsellMode: Dispatch<SetStateAction<UpsellMode | undefined>>;
  inAdsToggle: boolean;
  setInAdsToggle: Dispatch<SetStateAction<boolean>>;
}

const defaultContextValue: FabricInvitationContextType = {
  isPremiumOnMount: false,
  formValidations: {current: {}},
  setFormValidations: () => null,
  featureToggling: null,
  setFeatureToggling: () => null,
  upsellMode: undefined,
  showUpsellModal: false,
  setShowUpsellModal: () => null,
  setUpsellMode: () => null,
  inAdsToggle: false,
  setInAdsToggle: () => null,
};

export const FabricInvitationContext =
  createContext<FabricInvitationContextType>(defaultContextValue);

interface FabricInvitationContextParentProps {
  children: ReactNode;
}

export const FabricInvitationContextParent: FC<FabricInvitationContextParentProps> = ({
  children,
}) => {
  const formValidations = useRef<FormValidations>({});
  const [featureToggling, setFeatureToggling] = useState<EventFeature | null>(null);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [upsellMode, setUpsellMode] = useState<UpsellMode>();
  const [inAdsToggle, setInAdsToggle] = useState(false);

  const setFormValidations = useCallback(
    (validations: (current: FormValidations) => FormValidations) => {
      formValidations.current = validations(formValidations.current);
    },
    []
  );

  const contextValue: FabricInvitationContextType = useMemo(
    () => ({
      isPremiumOnMount: window.evite_event?.is_fabric_premium ?? window.fabricTemplate.premium,
      formValidations,
      setFormValidations,
      featureToggling,
      setFeatureToggling,
      showUpsellModal,
      setShowUpsellModal,
      upsellMode,
      setUpsellMode,
      inAdsToggle,
      setInAdsToggle,
    }),
    [setFormValidations, featureToggling, showUpsellModal, upsellMode, inAdsToggle]
  );

  return (
    <FabricInvitationContext.Provider value={contextValue}>
      {children}
    </FabricInvitationContext.Provider>
  );
};
